/**
 * Classification-dimension parse boundary (WSRADM-0025).
 *
 * THROW at the envelope (`ApiError` `UNKNOWN`); DROP at the row. `readOnly` is
 * the field only — never derived from `source`. `build` absent or null → null.
 */
import { ApiError } from '@/api/errors';
import { asBoolean, asNumber, asText, isObject } from '@/api/parse';
import { parseTriggerJobResponse } from '@/api/triggerJob';
import type { TriggerJobResponse } from '@/api/triggerJob';
import type {
  BoardFamily,
  BoardProduct,
  CatalogueStatus,
  DimensionBoard,
  DimensionBoardSaved,
  DimensionSource,
  DimensionStructure,
  DimensionStructureSaved,
  FamilyRole,
  Membership,
  StructureDimension,
  StructureFamily,
} from './types';

function parseStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    const text = asText(item);
    if (text !== null) out.push(text);
  }
  return out;
}

function parseChildren<T>(value: unknown, parseOne: (raw: unknown) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  const out: T[] = [];
  for (const item of value) {
    const parsed = parseOne(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseBuild(raw: Record<string, unknown>): TriggerJobResponse | null {
  const build = raw['build'];
  if (build === undefined || build === null) return null;
  return parseTriggerJobResponse(build);
}

function parseSource(value: unknown): DimensionSource {
  return value === 'WSTUDIO_CATEGORY' ? 'WSTUDIO_CATEGORY' : 'TENANT';
}

function parseRole(value: unknown): FamilyRole | null {
  return value === 'MEMBERSHIP' ? 'MEMBERSHIP' : null;
}

function parseStructureFamily(raw: unknown): StructureFamily | null {
  if (!isObject(raw)) return null;
  const slug = asText(raw['slug']);
  if (slug === null) return null;
  return {
    slug,
    displayName: asText(raw['displayName']) ?? '',
    sortOrder: asNumber(raw['sortOrder']) ?? 0,
    color: asText(raw['color']),
    role: parseRole(raw['role']),
    productCount: asNumber(raw['productCount']) ?? 0,
  };
}

function parseStructureDimension(raw: unknown): StructureDimension | null {
  if (!isObject(raw)) return null;
  const slug = asText(raw['slug']);
  if (slug === null) return null;
  return {
    slug,
    displayName: asText(raw['displayName']) ?? '',
    sortOrder: asNumber(raw['sortOrder']) ?? 0,
    source: parseSource(raw['source']),
    readOnly: raw['readOnly'] === true,
    families: parseChildren(raw['families'], parseStructureFamily),
  };
}

function requireStructureEnvelope(body: unknown): Record<string, unknown> {
  if (!isObject(body) || !Array.isArray(body['dimensions'])) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  return body;
}

export function parseDimensionStructure(body: unknown): DimensionStructure {
  const raw = requireStructureEnvelope(body);
  return { dimensions: parseChildren(raw['dimensions'], parseStructureDimension) };
}

export function parseDimensionStructureSaved(body: unknown): DimensionStructureSaved {
  const raw = requireStructureEnvelope(body);
  return {
    dimensions: parseChildren(raw['dimensions'], parseStructureDimension),
    build: parseBuild(raw),
  };
}

function parseBoardFamily(raw: unknown): BoardFamily | null {
  if (!isObject(raw)) return null;
  const slug = asText(raw['slug']);
  if (slug === null) return null;
  return {
    slug,
    displayName: asText(raw['displayName']) ?? '',
    color: asText(raw['color']),
    productCount: asNumber(raw['productCount']) ?? 0,
  };
}

const CATALOGUE_STATUSES: ReadonlySet<string> = new Set([
  'CURRENT',
  'RETIRED',
  'NOT_IN_CATALOGUE',
  'LEGACY',
]);

function parseCatalogueStatus(value: unknown): CatalogueStatus | null {
  return typeof value === 'string' && CATALOGUE_STATUSES.has(value)
    ? (value as CatalogueStatus)
    : null;
}

function parseBoardProduct(raw: unknown): BoardProduct | null {
  if (!isObject(raw)) return null;
  const sourceProductId = asNumber(raw['sourceProductId']);
  if (sourceProductId === null) return null;
  return {
    sourceProductId,
    name: asText(raw['name']),
    productType: asText(raw['productType']),
    isCurrent: asBoolean(raw['isCurrent']) ?? true,
    catalogueStatus: parseCatalogueStatus(raw['catalogueStatus']),
    durationDays: asNumber(raw['durationDays']),
    hasPendingVisits: asBoolean(raw['hasPendingVisits']),
    sourceSystem: asText(raw['sourceSystem']),
    wstudioCategories: parseStringList(raw['wstudioCategories']),
    familySlugs: parseStringList(raw['familySlugs']),
  };
}

function requireBoardEnvelope(body: unknown): {
  raw: Record<string, unknown>;
  dimension: Record<string, unknown>;
  slug: string;
} {
  if (!isObject(body)) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  const dimension = body['dimension'];
  if (!isObject(dimension)) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  const slug = asText(dimension['slug']);
  if (slug === null) {
    throw new ApiError('UNKNOWN', 0, {});
  }
  return { raw: body, dimension, slug };
}

function parseBoardFromEnvelope(
  raw: Record<string, unknown>,
  dimension: Record<string, unknown>,
  slug: string,
): DimensionBoard {
  return {
    dimension: {
      slug,
      displayName: asText(dimension['displayName']) ?? '',
      readOnly: dimension['readOnly'] === true,
    },
    families: parseChildren(raw['families'], parseBoardFamily),
    products: parseChildren(raw['products'], parseBoardProduct),
    unassignedCount: asNumber(raw['unassignedCount']) ?? 0,
  };
}

export function parseDimensionBoard(body: unknown): DimensionBoard {
  const { raw, dimension, slug } = requireBoardEnvelope(body);
  return parseBoardFromEnvelope(raw, dimension, slug);
}

export function parseDimensionBoardSaved(body: unknown): DimensionBoardSaved {
  const { raw, dimension, slug } = requireBoardEnvelope(body);
  return { ...parseBoardFromEnvelope(raw, dimension, slug), build: parseBuild(raw) };
}

export function parseMembership(raw: unknown): Membership | null {
  if (!isObject(raw)) return null;
  const dimensionSlug = asText(raw['dimensionSlug']);
  const familySlug = asText(raw['familySlug']);
  if (dimensionSlug === null || familySlug === null) return null;
  return {
    dimensionSlug,
    dimensionName: asText(raw['dimensionName']) ?? '',
    familySlug,
    familyName: asText(raw['familyName']) ?? '',
    color: asText(raw['color']),
  };
}

export function parseMembershipList(raw: unknown): Membership[] {
  return parseChildren(raw, parseMembership);
}
